// __EQUIP_COMPAT__ (2026-10-04) Reads for the equipment check + the override writer.
// The rules themselves are pure, in utils/equipmentCompat.js (twin of the hub's
// equipment_compat.py). Every assign path goes through components/EquipmentGate.js.
import { supabase } from '../supabaseClient';
import { resolveUnit, checkCompatibility, normalizeRules, todayISO, cleanSites, cleanPlaces, cleanTelematicsReadings } from '../utils/equipmentCompat';
import { driverDay, jobTime, sumDay } from './timingService';
import { getDefaultUnitSystem } from '../utils/units';

export const TRUCK_COLS = 'id, truck_number, truck_type, status, is_active, max_weight_kg, tare_weight_kg, payload_kg, gcwr_kg, '
  + 'cargo_length_cm, cargo_width_cm, cargo_height_cm, deck_height_cm, features, axle_count, half_load_total_kg, half_load_axle_caps, make, model, year';
export const TRAILER_COLS = 'id, trailer_number, trailer_type, status, is_active, max_weight_kg, tare_weight_kg, payload_kg, '
  + 'cargo_length_cm, cargo_width_cm, cargo_height_cm, deck_height_cm, features, axle_count, half_load_total_kg, half_load_axle_caps, make';
export const COMBO_COLS = 'id, combination_name, truck_id, trailer_id, default_driver_id, is_default, combined_max_weight_kg, created_at';
export const ASSIGN_COLS = 'id, driver_id, truck_id, is_primary, start_date, end_date, note';
// billing_data: __TRUCK_REQUIRED__ (tarp / closed truck) lives there
// __SITE_LOADING__ the stops' addresses / place names (address-book "Loading at this site")
const LOAD_COLS = 'id, load_number, status, driver_id, truck_id, weight_kg, length_cm, width_cm, height_cm, commodity, notes, scheduled_date, estimated_duration_hours, billing_data, '
  + 'pickup_address, delivery_address, '
  + 'load_stops(id, load_id, stop_type, stop_sequence, planned_arrival_at, planned_departure_at, planned_leg_min, planned_prep_min, planned_dwell_min, address, place_name, latitude, longitude)';   // __OPERATING_LIMITS__ where the stops are

const TTL_MS = 30000;
let cache = { at: 0, fleet: null, rulesAt: 0, rules: null };

export function invalidateEquipmentCache() { cache = { at: 0, fleet: null, rulesAt: 0, rules: null }; }

// { drivers, trucks, trailers, combos, assignments } - or { ..., error } when a read failed.
// __EQUIP_ASSIGN__ assignments = driver_equipment_assignments (a missing table / failed read of
// it alone = [] - the old one-unit-per-driver fields are then used).
export async function fetchFleet(force = false) {
  if (!force && cache.fleet && Date.now() - cache.at < TTL_MS) return cache.fleet;
  const [d, t, r, c, a] = await Promise.all([
    supabase.from('drivers').select('id, name, current_truck_id, is_available'),
    supabase.from('trucks').select(TRUCK_COLS),
    supabase.from('trailers').select(TRAILER_COLS),
    supabase.from('truck_trailer_combinations').select(COMBO_COLS),
    supabase.from('driver_equipment_assignments').select(ASSIGN_COLS),
  ]);
  const err = d.error || t.error || r.error || c.error;
  const fleet = { drivers: d.data || [], trucks: t.data || [], trailers: r.data || [], combos: c.data || [],
    assignments: (a && !a.error && a.data) || [] };
  if (err) { fleet.error = err.message || String(err); return fleet; }
  cache.fleet = fleet; cache.at = Date.now();
  return fleet;
}

// Settings > Equipment rules (public.equipment_rules, row 'main'). Defaults when absent.
export async function fetchRules(force = false) {
  if (!force && cache.rules && Date.now() - cache.rulesAt < TTL_MS) return cache.rules;
  let raw = null;
  try {
    const { data, error } = await supabase.from('equipment_rules').select('data').eq('id', 'main').maybeSingle();
    if (!error && data) raw = data.data;
  } catch (e) { /* defaults */ }
  cache.rules = normalizeRules(raw); cache.rulesAt = Date.now();
  return cache.rules;
}

export async function saveRules(rules, opts = {}) {
  const clean = normalizeRules(rules);
  // __SITE_LOADING__ the sites belong to the hub (address book / portal places "Loading at this site", mirrored into this
  // row): keep what the row holds NOW, never an older copy from an open Settings page
  try {
    const { data: cur } = await supabase.from('equipment_rules').select('data').eq('id', 'main').maybeSingle();
    clean.sites = cleanSites(((cur && cur.data) || {}).sites);
    clean.places = cleanPlaces(((cur && cur.data) || {}).places);   // __OPERATING_LIMITS__ the address book's names (hub)
    clean.telematics_readings = cleanTelematicsReadings(((cur && cur.data) || {}).telematics_readings);   // __TELEMATICS__ hub-owned
    // __OPERATING_LIMITS__ each truck's limits and the city zones are saved by their own editors (updateRules, from a fresh
    // read): the Settings page's Save keeps what the row holds NOW, never its older copy
    if (!opts.fresh && cur) {
      const now = normalizeRules((cur && cur.data) || null);
      clean.unit_limits = now.unit_limits;
      clean.city_zones = now.city_zones;
      clean.telematics_units = now.telematics_units;   // __TELEMATICS__ saved by the truck form
    }
  } catch (e) { /* keep what was read */ }
  const { data: { user } = {} } = await supabase.auth.getUser();
  const { error } = await supabase.from('equipment_rules')
    .upsert({ id: 'main', data: clean, updated_at: new Date().toISOString(), updated_by: user ? user.id : null });
  if (error) throw error;
  cache.rules = clean; cache.rulesAt = Date.now();
  return clean;
}

// __UNIT_DOWNTIME__ __SELF_UNLOAD__ (2026-10-08) read the rules fresh, change one thing, save: the dated
// downtime schedule (Trucks / Trailers Edit, Equipment Assignment) and the drivers' "Can self-unload"
// (Drivers) live in this row (utils/equipmentDowntime.js), so nothing waits on a migration.
export async function updateRules(change) {
  const cur = await fetchRules(true);
  return saveRules(change(cur), { fresh: true });
}

// Stops of the given jobs in driving order: stopIds (a route's order) when given,
// else each job's pickups then drop-offs, jobs in the order given.
export function stopsInOrder(loads, stopIds) {
  const all = [];
  (loads || []).forEach((l) => (l.load_stops || []).forEach((s) => all.push({ ...s, load_id: s.load_id || l.id })));
  if (Array.isArray(stopIds) && stopIds.length) {
    const byId = {}; all.forEach((s) => { byId[s.id] = s; });
    const out = stopIds.map((id) => byId[id]).filter(Boolean);
    if (out.length) return out.map((s) => ({ load_id: s.load_id, stop_type: s.stop_type }));
  }
  const out = [];
  (loads || []).forEach((l) => {
    const st = (l.load_stops || []).slice().sort((a, b) => (a.stop_sequence || 0) - (b.stop_sequence || 0));
    st.filter((s) => s.stop_type === 'pickup').forEach((s) => out.push({ load_id: l.id, stop_type: 'pickup' }));
    st.filter((s) => s.stop_type !== 'pickup').forEach((s) => out.push({ load_id: l.id, stop_type: s.stop_type || 'delivery' }));
  });
  return out;
}

// The one check every assign path runs.
//   driverId          : who gets the job(s)
//   loads | loadIds   : the job rows (weight/sizes/cargo) or their ids (read fresh)
//   stopIds           : a route's stop order (Route Builder); optional
//   truckId           : a truck picked with the assignment (Quick Assign / AI); optional
//   date              : the job / route day (temporary restrictions, half-load season, timing);
//                       default the first job's scheduled_date, else today
//   added             : the time the work takes ({ onDutyMin, driveMin, start, end, source });
//                       default the jobs' own times (route plan / estimate / 2 h rough)
//   withTiming        : false to skip the day / HOS part
// -> checkCompatibility result + { driverId, driverName, unchecked, date }
export async function checkAssignment({ driverId, loads, loadIds, stopIds, truckId, date, added, withTiming = true } = {}) {
  let rows = loads;
  if ((!rows || !rows.length) && loadIds && loadIds.length) {
    const { data } = await supabase.from('loads').select(LOAD_COLS).in('id', loadIds);
    rows = data || [];
  }
  rows = rows || [];
  const [fleet, rules] = await Promise.all([fetchFleet(), fetchRules()]);
  if (fleet.error) {
    return { ok: true, canAssign: true, needsReason: false, unchecked: true, stops: [], blocking: [], warnings: [], infos: [], issues: [],
      unit: null, peakLb: null, peakNote: null, timingNote: null, driverId, driverName: null, readError: fleet.error };
  }
  const day = /^\d{4}-\d{2}-\d{2}$/.test(String(date || '')) ? date
    : ((rows.find((l) => l.scheduled_date) || {}).scheduled_date || todayISO());
  // only a truck picked WITH this assignment counts: loads.truck_id is left over from
  // whoever had the job before (a reassignment does not clear it)
  const unit = resolveUnit({ driverId, truckId: truckId || null, fleet, day });
  const driver = fleet.drivers.find((d) => d.id === driverId);
  const multi = rows.length > 1;
  const stops = multi ? stopsInOrder(rows, stopIds) : null;
  let timing = null;
  if (withTiming && driver) {
    try {
      const dd = await driverDay(driverId, day, rows.map((l) => l.id).filter(Boolean));
      timing = { existing: dd.existing, added: added || sumDay(rows.map(jobTime)), prevDayEnd: dd.prevDayEnd };
    } catch (e) { timing = null; }
  }
  const res = checkCompatibility({ unit, loads: rows, stops, rules, units: getDefaultUnitSystem(), driverName: driver ? driver.name : null,
    date: day, driverMissing: !driver, timing, driverId });   // __SELF_UNLOAD__ his abilities
  // __EQUIP_ASSIGN__ shared units: when it is not clear which one he takes, the gate asks
  const unitChoices = !truckId && unit && Array.isArray(unit.choices) && unit.choices.length > 1 ? unit.choices : null;
  return { ...res, driverId, driverName: driver ? driver.name : null, unchecked: false, date: day, unitChoices,
    pickedTruckId: truckId || (unit && unit.truck ? unit.truck.id : null) };
}

// Route Builder Finalize: every available driver checked against the route, split into
// who qualifies (nothing above a hint) and who does not, each with his unit, his issues
// and his day. added = the route's own time (timelineTime of the draft timeline).
export async function qualifyDrivers({ drivers, loads, stopIds, date, added }) {
  const list = await Promise.all((drivers || []).map(async (d) => {
    const r = await checkAssignment({ driverId: d.id, loads, stopIds, date, added });
    return { driver: d, result: r };
  }));
  const rank = (x) => (x.result.ok ? 0 : 1) * 1000 + x.result.stops.length * 100 + x.result.blocking.length * 10 + x.result.warnings.length;
  list.sort((a, b) => rank(a) - rank(b) || String(a.driver.name || '').localeCompare(String(b.driver.name || '')));
  return { qualifies: list.filter((x) => x.result.ok), notQualified: list.filter((x) => !x.result.ok) };
}

// Synchronous hint for badges (driver chips, dropdowns) from an already-read fleet (no timing).
export function quickHint({ driverId, loads, fleet, rules, truckId, date }) {
  if (!fleet || fleet.error || !rules || !loads || !loads.length) return null;
  const day = date || ((loads.find((l) => l.scheduled_date) || {}).scheduled_date) || todayISO();
  const unit = resolveUnit({ driverId, truckId: truckId || null, fleet, day });
  const driver = (fleet.drivers || []).find((d) => d.id === driverId);
  const res = checkCompatibility({ unit, loads, stops: loads.length > 1 ? stopsInOrder(loads) : null, rules, units: getDefaultUnitSystem(),
    date: day, driverMissing: !driver, driverId });   // __SELF_UNLOAD__
  const level = !res.canAssign || res.needsReason ? 'block' : !res.ok ? 'warn' : 'ok';
  const mark = (x) => (x.severity === 'stop' || x.severity === 'block' ? 'X ' : x.severity === 'warn' ? '! ' : '- ');
  return { level, result: res, title: res.issues.length ? res.issues.map((x) => mark(x) + x.message).join('\n') : 'Equipment fits' };
}

// "Assign anyway": the override goes onto each job (billing_data.equipment_override,
// keeping billing_data's stored form) and into the hub's audit log, in one database
// call (public.record_equipment_override, office logins only).
export async function recordOverride({ loadIds, override }) {
  if (!override || !loadIds || !loadIds.length) return { ok: true, skipped: true };
  const { data, error } = await supabase.rpc('record_equipment_override', { p_load_ids: loadIds, p_override: override });
  if (error) { console.warn('equipment override not recorded:', error.message); return { ok: false, error: error.message }; }
  return { ok: true, count: data };
}
